import { NPI_SUGGESTION_STAGES, type NpiSuggestionStage } from './types';

/**
 * A hint from a run on the person's `/npi/{sub}/{runId}` channel. It names
 * the run and, for progress, its stage; the suggestions are read from the API.
 */
export type NpiRunEvent =
  | { type: 'progress'; runId: string; stage: NpiSuggestionStage }
  | { type: 'completed'; runId: string };

const RUN_ID = /^[0-9a-f]{12}$/;

/** The channel a run's hints arrive on. */
export function npiRunChannel(sub: string, runId: string): string {
  return `/npi/${sub}/${runId}`;
}

/**
 * Reads a hint, throwing on anything that is not one, which drops it. Only
 * the fields the screen acts on are read, so the server can add one without
 * breaking older clients.
 */
export function parseNpiRunEvent(raw: unknown): NpiRunEvent {
  const event = raw as { v?: unknown; type?: unknown; data?: Record<string, unknown> };

  const runId = event?.data?.runId;

  if (event?.v !== 1 || typeof runId !== 'string' || !RUN_ID.test(runId)) {
    throw new Error('Not an NPI run event');
  }

  if (event.type === 'run.completed') {
    return { type: 'completed', runId };
  }

  const stage = event.data?.stage;

  if (
    event.type === 'run.progress' &&
    typeof stage === 'string' &&
    (NPI_SUGGESTION_STAGES as readonly string[]).includes(stage)
  ) {
    return { type: 'progress', runId, stage: stage as NpiSuggestionStage };
  }

  throw new Error('Not an NPI run event');
}
